<template>
  <div class="wt2-slack wt2-modal-inner -slack-inner">
    <div
      v-if="showOverlay"
      class="-slack-overlay"
      :class="{
        '-no-alpha': loading,
      }"
    >
      <div class="-center">
        <div v-if="loading" class="wt-loader"></div>
        <div
          v-else-if="sendingPost"
          class="-circle-loader"
          :class="{
            'load-complete': sendingComplete,
          }"
        >
          <div
            class="checkmark draw"
            :style="{
              display: sendingComplete ? 'block' : 'none',
            }"
          ></div>
        </div>
        <div class="-overlay-title">
          <span>{{
            (loading ? 'slack.loading' : 'slack.sendingPost') | culture
          }}</span>
          <span
            class="-dots"
            v-animate-sequence="{ cancelAnimation: sendingComplete }"
            >...</span
          >
        </div>
      </div>
    </div>
    <div class="-title wt2-text-l2">{{ 'slack.modalTitle' | culture }}</div>
    <div v-if="showActionFailedError" class="-slack-failed-action">
      <span class="-message wt2-color-error">{{
        'slack.actionFailed' | culture
      }}</span>
      <a class="-contact wt2-color-error" @click="contactSupport">{{
        'slack.contactSupport' | culture
      }}</a>
    </div>
    <div v-if="loggedIn" class="-slack-share">
      <div class="-slack-form">
        <div class="-slack-form-col -first">
          <div class="-row-label wt2-text-n2">
            <span>{{ 'slack.sendToLabel' | culture }}</span>
          </div>
          <div class="-row-label wt2-text-n2">
            <span>{{ 'slack.messageLabel' | culture }}</span>
          </div>
        </div>
        <div class="-slack-form-col -second">
          <div class="-row-input">
            <wt-add-keywords
              :class="{
                '-error': showMissingKeywordsError,
              }"
              theme="black-theme text-input-theme"
              ref="keywords"
              :keywords="displayedSelectedRecipients"
              :empty-keywords-placeholder="
                (showMissingKeywordsError
                  ? 'slack.keywordsMissingPlaceholder'
                  : 'slack.keywordsPlaceholder') | culture
              "
              has-keywords-placeholder
              :delimiters="[]"
              :disabled="sendingPost"
              @addkeyword="onSearchRecipientInputEvent"
              @keydown.up.down.enter.esc="controlList"
              @click="openList"
              v-click-outside="closeList"
              @removekeyword="removeRecipient"
            ></wt-add-keywords>
            <div
              class="-slack-recipients-list"
              ref="list"
              v-show="showRecipientsList && !sendingPost"
            >
              <ul
                v-if="displayedMenuRecipients && displayedMenuRecipients.length"
              >
                <li
                  v-for="(r, i) in displayedMenuRecipients"
                  @mouseenter="activeListIndex = i"
                  :key="r.id"
                >
                  <div
                    class="-row"
                    :data-active="activeListIndex === +i"
                    :data-id="r.id"
                    @click.prevent="
                      addRecipient(r);
                      retainFocus();
                    "
                  >
                    <template v-if="r.displayName || r.name">
                      <span
                        class="-name wt2-text-n2 wt2-color-normal"
                        v-html="r.displayName || r.name"
                      ></span>
                      <span class="-real-name wt2-text-n">{{
                        r.realName
                      }}</span>
                    </template>
                    <template v-else>
                      <span class="-name wt2-text-n2 wt2-color-normal">{{
                        r.realName
                      }}</span>
                    </template>
                  </div>
                </li>
              </ul>
              <ul class="-no-match" v-else>
                <li>
                  <span class="wt2-text-n">{{
                    'slack.noMatch' | culture
                  }}</span>
                </li>
              </ul>
            </div>
            <div class="-slack-instruction">
              <span class="wt2-text-s">{{
                'slack.searchInstruction' | culture
              }}</span>
            </div>
          </div>
          <div class="-row-input">
            <textarea
              ref="textarea"
              @click="$refs.textarea.select()"
              :placeholder="'slack.messagePlaceholder' | culture"
              class="wt2-textarea"
              v-model="message"
            ></textarea>
          </div>
        </div>
      </div>
      <div class="-slack-actions" v-tab-guard>
        <wt-dropdown
          type="normal fixed menu-up"
          class="-slack-teams"
          :items="extendedTeams"
          :num-items="4"
          :current-item-id="currentTeamId"
          :disabled="sendingPost"
          :on-click-item="onChangeTeam"
        >
          <template slot="button" slot-scope="props">
            <span class="wt2-dropdown-label">Team:</span>
            <span class="text-three-dots">{{ props.currentItem.label }}</span>
            <i class="wticon wticon-chevron-down"></i>
          </template>
          <template slot="item" slot-scope="props">
            <div>
              <span class="-slack-add-team" v-if="props.item.id === -1">
                <i class="wticon wticon-plus-thick"></i>
                {{ 'slack.addTeamButton' | culture }}
              </span>
              <span v-else>{{ props.item.label }}</span>
            </div>
          </template>
        </wt-dropdown>
        <wt-button
          class="-slack-close -min-width-unset"
          tabindex="-1"
          type="transparent"
          :text="'generic.CANCEL' | culture"
          :disabled="sendingPost"
          @click="closeModal"
        ></wt-button>
        <wt-button
          :text="'slack.postButton' | culture"
          tabindex="0"
          :disabled="sendingPost"
          @click="post"
        ></wt-button>
      </div>
    </div>
    <div v-else class="-slack-login">
      <div class="-slack-caption">
        <span>{{ 'slack.loginCaption' | culture }}</span>
      </div>
      <div class="-slack-actions" v-tab-guard>
        <wt-button
          :text="'slack.connectButton' | culture"
          tabindex="0"
          @click="connect"
        ></wt-button>
        <wt-button
          class="-slack-close -min-width-unset"
          tabindex="-1"
          type="transparent"
          :text="'generic.CANCEL' | culture"
          @click="closeModal"
        ></wt-button>
      </div>
    </div>
  </div>
</template>

<script>
import app from 'app';

export default {
  analytics: {
    'slack:connected': 'Connected to Slack',
    'slack:sharing': 'Posted to Slack',
  },

  props: {
    closeModal: {
      type: Function,
      required: true,
    },

    video: {
      type: Object,
      required: true,
    },

    videoType: {
      type: String,
      required: true,
      validator(value) {
        return ['produced', 'draft', 'ready_to_publish', 'storyboard'].includes(
          value
        );
      },
    },
  },

  beforeCreate() {
    this.$options.types = Object.freeze({
      CONVERSATIONS: 'conversations',
      MEMBERS: 'members',
      CHANNELS: 'channels',
      GROUPS: 'groups',
    });
  },

  created() {
    this.load({
      teamId: app.stash.get('slackSelectedTeamId'),
    });
  },

  data() {
    return {
      userTeams: null,
      selectedRecipients: [],
      showRecipientsList: false,
      rawRecipients: [],
      currentTeamId: null,
      activeListIndex: 0,
      sendingPost: false,
      sendingComplete: false,
      loading: false,
      loggedIn: false,
      showMissingKeywordsError: false,
      showActionFailedError: false,
      curInputValue: null,
      message: '',
    };
  },

  watch: {
    showRecipientsList(isOpen) {
      // if just been closed, reset list
      if (!isOpen) {
        this.resetList();
      }
    },

    curInputValue(...args) {
      const oldVal = args[1];
      // if the list input value has changed and the list is open
      // and the old val is not null (not first time) then reset
      if (this.showRecipientsList && !_.isNull(oldVal)) {
        this.resetList();
      }
    },

    currentTeamId(teamId) {
      app.stash.set('slackSelectedTeamId', teamId);
    },
  },

  computed: {
    teamsArr() {
      return _.map(this.userTeams, (val, key) => {
        return {
          id: key,
          label: val,
        };
      });
    },

    extendedTeams() {
      return [...this.teamsArr, { id: -1 }];
    },

    normalizedRecipients() {
      const members = this.findMembers(this.rawRecipients);

      return _.flatten(
        _.map(this.rawRecipients, (val) => {
          const firstKey = _.keys(val)[0];
          const firstValue = val[firstKey] || [];

          // if firstValue it's array return mapped array
          if (_.isArray(firstValue)) {
            return this.mapRawValues(firstValue, firstKey, members);
          }

          // if not, do a second flatten, and return mapped array
          return this.mapRawValues(
            _.flatten(_.toArray(firstValue)),
            firstKey,
            members
          );
        })
      );
    },

    filteredRecipients() {
      const curInputValue = (this.curInputValue || '').toLowerCase();
      const startWithAt = _.startsWith(curInputValue, '@', 0);
      const startWithHashTag = _.startsWith(curInputValue, '#', 0);
      const types = this.$options.types;

      return _.filter(this.normalizedRecipients, (r) => {
        // special use case for conversations (recent)
        // if no input value, return all of them
        if (curInputValue === '') {
          return this.testType(types.CONVERSATIONS, r, r.type);
        } else if (startWithAt) {
          // return all of members or just matched ones
          return (
            this.testType(types.MEMBERS, r) &&
            (curInputValue.length > 1
              ? this.isStartsWith(
                  r,
                  curInputValue.substring(1, curInputValue.length)
                )
              : true)
          );
        } else if (startWithHashTag) {
          // return all of channels or just matched ones
          return (
            this.testType(types.CHANNELS, r) &&
            (curInputValue.length > 1
              ? this.isStartsWith(
                  r,
                  curInputValue.substring(1, curInputValue.length)
                )
              : true)
          );
        }
        // return all if match
        return this.isStartsWith(r, curInputValue);
      });
    },

    sortedRecipients() {
      const arr = _.orderBy(
        this.filteredRecipients,
        [
          'name',
          'realName',
          (r) => {
            return this.testType(this.$options.types.CONVERSATIONS, r, r.type);
          },
        ],
        ['asc', 'asc', 'desc']
      );

      // returns unique array to prevent duplication
      // only after sort so conversations will be the first,
      // and duplication will be removed from members or channels
      return _.uniqBy(arr, 'id');
    },

    displayedSelectedRecipients() {
      return _.map(this.selectedRecipients, (r) => {
        return r.name || r.realName;
      });
    },

    displayedMenuRecipients() {
      return _.map(this.sortedRecipients, (r) => {
        const isChannel = this.testType(this.$options.types.CHANNELS, r);
        const isGroup = this.testType(this.$options.types.GROUPS, r);

        if (isChannel || isGroup) {
          r = Object.assign({}, r, {
            displayName: (isChannel ? '#' : '&#128274;') + r.name,
          });
        }

        return r;
      });
    },

    showOverlay() {
      return this.loading || this.sendingPost;
    },
  },

  methods: {
    connect() {
      app.api.socialProvider
        .connect(this.video, app.api.socialProvider.providers.SLACK)
        .done((data) => {
          // eslint-disable-next-line prefer-rest-params
          console.log('slack: connect success!', arguments);
          this.load({ teamId: data.providerUserId, cache: false });
          this.$triggerAnalytics('slack:connected');
        })
        .fail((err) => {
          if (err && err.isCanceled) {
            // eslint-disable-next-line prefer-rest-params
            console.log('slack: connect canceled!', arguments);
          } else {
            // eslint-disable-next-line prefer-rest-params
            console.log('slack: connect error!', arguments);
            app.dialog.error({
              type: app.dialog.types.ERROR_1,
              title: 'Action Failed',
              description: 'Unexpected error occurred. </br>',
              showContactCsm: true,
            });
          }
        });
    },

    post() {
      if (_.isEmpty(this.selectedRecipients)) {
        this.showMissingKeywordsError = true;
        return;
      }

      this.sendingPost = true;
      this.sendingComplete = false;
      app.api.socialProvider
        .post({
          endpoint: app.api.socialProvider.providers.SLACK + '/messages',
          data: {
            channels: _.map(this.selectedRecipients, (r) => r && r.id),
            comment: this.message,
            videoType: this.videoType,
            videoId: this.video.id,
          },
          params: {
            team: this.currentTeamId,
          },
        })
        .done(() => {
          // eslint-disable-next-line prefer-rest-params
          console.log('slack: post success!', arguments);
          this.$triggerAnalytics('slack:sharing', {
            SharedVideo: this.videoType,
            HasComment: !_.isEmpty(this.message),
          });
          this.sendingComplete = true;
          setTimeout(() => {
            this.closeModal();
            this.sendingPost = false;
          }, 1500);
        })
        .fail((e) => {
          // eslint-disable-next-line prefer-rest-params
          console.error('slack: post error!', arguments);
          this.showActionFailedError = true;
          this.errorHandling(e);
          this.sendingPost = false;
          this.sendingComplete = true;
        });
    },

    errorHandling(e) {
      if (e) {
        switch (e.status) {
          case 401: // Unauthorized
            this.loggedIn = false;
            break;
        }
      }
    },

    testType(type, r, str) {
      str = str || r.innerType || r.type;
      return new RegExp(`${type}?$`).test(str);
    },

    load(options) {
      options = Object.assign(
        {
          teamId: null,
          cache: false,
        },
        options
      );

      this.getAccount({ cache: options.cache })
        .then((account) => {
          this.userTeams = (account && account.teams) || {};
          const currentTeam = _.find(this.teamsArr, (t) => {
            return options.teamId ? t && t.id === options.teamId : t && t.id; // get the first one with id
          });

          if (currentTeam && currentTeam.id) {
            this.currentTeamId = currentTeam.id;
          } else {
            if (this.teamsArr.length) {
              const firstItem = _.find(this.teamsArr, (t) => t && t.id);
              this.currentTeamId = firstItem.id;
            } else {
              this.errorHandling({ status: 401 });
            }
          }
        })
        .then(() => {
          this.loadData({ cache: options.cache });
        })
        .catch((e) => {
          this.errorHandling(e);
        });
    },

    getAccount({ cache } = {}) {
      this.loading = true;

      return new Promise((resolve, reject) => {
        app.api.socialProvider
          .getUserAccounts({
            cache,
          })
          .then((accounts) => {
            this.loggedIn = !_.isEmpty(accounts.slack);

            return this.loggedIn
              ? resolve(accounts.slack)
              : reject({ status: 401 });
          })
          .fail((e) => {
            this.loggedIn = false;
            reject(e);
          })
          .always(() => {
            this.loading = false;
          });
      });
    },

    loadData({ cache } = {}) {
      const obj = {
        params: { team: this.currentTeamId },
        cache,
        maxAge: app.TIME.MINUTE * 10,
      };
      const providerName = app.api.socialProvider.providers.SLACK;
      const types = this.$options.types;
      const endpoints = [types.CONVERSATIONS, types.MEMBERS, types.CHANNELS];

      this.rawRecipients = [];
      this.selectedRecipients = [];
      // invoke each promise separately, instead of in Promise.all
      // so to be able to use one or more if some are failing
      endpoints.forEach((endpoint) => {
        app.api.socialProvider
          .get(Object.assign({ endpoint: providerName + '/' + endpoint }, obj))
          .done((data) => {
            // concat creates new array, which invoke computed normalizedRecipients
            const raw = _.pick(data, endpoint);
            this.rawRecipients = (this.rawRecipients || []).concat(raw);
          })
          .fail((e) => {
            this.errorHandling(e);
          });
      });
    },

    onChangeTeam(teamId) {
      const clickedAddTeam = teamId === -1;

      if (clickedAddTeam) {
        this.connect();
      } else {
        this.currentTeamId = teamId;
        this.loadData({ cache: false });
      }
    },

    onSearchRecipientInputEvent(keyword) {
      this.showMissingKeywordsError = false;
      if (!_.isEmpty(keyword)) {
        this.openList();
      }
      // wait so filteredRecipients will be rendered after the first dom update
      this.$nextTick(() => {
        this.curInputValue = keyword;
      });
    },

    addRecipient(r) {
      // just to make sure it's a valid recipient
      if (r && r.id && (r.name || r.realName)) {
        const newArr = this.selectedRecipients.concat(r);
        this.selectedRecipients = _.uniq(newArr, 'id');
      }
    },

    removeRecipient(tagName) {
      this.selectedRecipients = _.filter(
        this.selectedRecipients,
        (r) => (r.name || r.realName) !== tagName
      );
    },

    isStartsWith(r, curInputValue) {
      const name = r && r.name && r.name.toLowerCase();
      const realName = r && r.realName && r.realName.toLowerCase();

      return (
        _.startsWith(name, curInputValue) ||
        _.startsWith(realName, curInputValue)
      );
    },

    extendUser(r, members) {
      const userMember = _.find(members, (m) => m.id === r.id);

      return _.assign({}, r, {
        name: (userMember && userMember.name) || null,
      });
    },

    mapRawValues(value, key, members) {
      return _.filter(
        _.map(value, (r) => {
          // for conversations for example
          if (!r.name && r.user && members.length) {
            r = this.extendUser(r, members);
          }

          return _.assign({}, _.pick(r, 'id', 'name', 'realName'), {
            type: key,
            // innerType can be the same as type or different
            // the reason for it when it's different, we need to use
            // the innerType to test recipient type like in conversations
            innerType: r.type,
          });
        }),
        (r) => r.id && (r.name || r.realName)
      );
    },

    openList() {
      this.showRecipientsList = true;
    },

    closeList() {
      this.showRecipientsList = false;
    },

    findMembers(arr) {
      const type = this.$options.types.MEMBERS;
      const members = _.filter(arr, (r) => _.has(r, type));

      return members.length
        ? // using flatten to ensure the value will be an array
          _.flatten([members[0][type]])
        : [];
    },

    controlList(e) {
      // if the list is open
      if (this.showRecipientsList) {
        e.preventDefault();
        const list = this.$refs.list;
        const length = this.displayedMenuRecipients.length;

        switch (e.keyCode) {
          // down arrow
          case 40: {
            const isLastRow = length - 1 === this.activeListIndex;
            if (isLastRow) {
              this.activeListIndex = 0;
              this.scrollTo({ behavior: 'instant' });
            } else {
              this.activeListIndex = this.activeListIndex + 1;
              this.scrollTo();
            }
            break;
          }
          // up arrow
          case 38: {
            const isFirstInRow = this.activeListIndex === 0;
            if (isFirstInRow) {
              this.activeListIndex = length - 1;
              this.scrollTo({ behavior: 'instant' });
            } else {
              this.activeListIndex = this.activeListIndex - 1;
              this.scrollTo();
            }
            break;
          }
          // escape
          case 27:
            this.closeList();
            // using defineProperty so by default it will not be writable
            // and there for can't be changed, but can be deleted
            Object.defineProperty(e, '__WT_STOP_PROPAGATION__', {
              value: true,
            });
            // remove input focus
            this.removeFocus();
            break;
          // enter
          case 13: {
            const activeEl = list.querySelector('[data-active]');

            if (activeEl && activeEl.dataset.id) {
              const findR = _.find(
                this.displayedMenuRecipients,
                (r) => r.id === activeEl.dataset.id
              );

              if (findR) {
                this.addRecipient(findR);
                this.closeList();
              }
            }
            break;
          }
        }
      } else {
        // if the list is closed
        if (e.keyCode === 40 /* down arrow */) {
          this.openList();
        }
      }
    },

    resetList() {
      this.activeListIndex = 0;
      this.$refs.list.scrollTop = 0;
    },

    getKeywordsInput() {
      const keywords = this.$refs.keywords;
      const keywordsEl = keywords && keywords.$el;
      return keywordsEl && keywordsEl.querySelector('input');
    },

    removeFocus() {
      const input = this.getKeywordsInput();
      input && input.blur();
    },

    retainFocus() {
      const input = this.getKeywordsInput();
      input && input.focus();
    },

    scrollTo(options) {
      options = Object.assign(
        {
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        },
        options
      );

      // wait till data-active will be re render to the new active row
      this.$nextTick(() => {
        const el = this.$refs.list.querySelector('[data-active]');

        el &&
          el.scrollIntoView({
            behavior: options.behavior,
            block: options.block,
            inline: options.inline,
          });
      });
    },

    contactSupport() {
      const message = app.culture
        .get('slack.errorIntercomMessage')
        .format(this.video.id);
      app.intercomController.showNewMessage(message).catch(() => {
        // fallback - if there's no intercom, go to website support
        app.openSupportTab();
      });
    },
  },
};
</script>
